import { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  CERT_KINDS,
  CERT_LABEL,
  STAGE_RESULT_TYPE_LABEL,
  certAttendancePct,
  certKindOf,
  deriveCertificate,
  effectiveCertificate,
  stageLabel,
  type StageResultType,
} from '@doit/shared';
import { Award, Globe, Pencil, Plus, Trash2 } from 'lucide-react';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { Button, Card, EmptyState } from '../../components/ui';
import { Field } from '../../components/ui';
import { Checkbox, Select, Textarea } from '../../components/form';
import { Badge } from '../../components/badges';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../api/client';
import { useDeleteStageResult, useSaveStageSet } from '../../api/queries';
import type { CardEnrollment, StageResult } from '../../api/types';
import { formatDate } from '../../lib/format';

/**
 * ეტაპის დასწრება სერტიფიკატისთვის — მიმდინარე სტატუსით (დასწრებული / ჩატარებული);
 * ეტაპის ბოლოს საბოლოოა. ეტაპი ჟურნალში თუ არ არის (CSV-იმპორტი) — იმპორტირებული %.
 * იგივე წესი, რაც სერვერზე (`lib/certificate.ts`).
 */
function stagePct(enrollment: CardEnrollment, stage: number, rows: StageResult[]): number | null {
  const points = (enrollment.stats?.timeline ?? []).filter((p) => p.stage === stage);
  if (points.length > 0) {
    return certAttendancePct({
      present: points.filter((p) => p.status === 'PRESENT').length,
      marked: points.length,
    });
  }
  const imported = rows.map((r) => r.attendancePct).filter((v): v is number => v !== null);
  return imported.length > 0 ? Math.max(...imported) : null;
}

export function StageResults({
  studentId,
  enrollment,
  canEdit,
  lecturerMode = false,
}: {
  studentId: string;
  enrollment: CardEnrollment;
  canEdit: boolean;
  /** ლექტორი: მხოლოდ მიმდინარე ეტაპი, ქულისა და სერტიფიკატის ხელით შეცვლის გარეშე */
  lecturerMode?: boolean;
}) {
  const toast = useToast();
  const remove = useDeleteStageResult(studentId);
  const [editing, setEditing] = useState<number | null>(null);
  const [toDelete, setToDelete] = useState<StageResult | null>(null);

  const stageCount = enrollment.group.course.stageCount;
  // ეტაპზე შეიძლება რამდენიმე ჩანაწერი იყოს: დავალება + პროექტი (+ ქულა)
  const byStage = new Map<number, StageResult[]>();
  for (const r of enrollment.stageResults) {
    const list = byStage.get(r.stage) ?? [];
    list.push(r);
    byStage.set(r.stage, list);
  }

  const stages = lecturerMode
    ? [enrollment.currentStage]
    : Array.from({ length: stageCount }, (_, i) => i + 1);

  async function handleDelete() {
    if (!toDelete) return;
    try {
      await remove.mutateAsync(toDelete.id);
      toast.success('შედეგი წაიშალა');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'წაშლა ვერ მოხერხდა');
    } finally {
      setToDelete(null);
    }
  }

  const editingRows = editing !== null ? (byStage.get(editing) ?? []) : [];

  return (
    <>
      <Card
        title={lecturerMode ? `შეფასება · ${stageLabel(enrollment.currentStage)}` : 'ეტაპის შედეგები'}
        action={
          lecturerMode && canEdit ? (
            <Button
              size="sm"
              variant="secondary"
              icon={<Pencil className="size-4" />}
              onClick={() => setEditing(enrollment.currentStage)}
            >
              შეფასება
            </Button>
          ) : undefined
        }
      >
        {lecturerMode ? (
          /* --- ლექტორი: რა არის მონიშნული და რა ეწერება სერტიფიკატზე --- */
          (() => {
            const rows = byStage.get(enrollment.currentStage) ?? [];
            const marked = rows.filter((r) => r.resultType === 'TASK' || r.resultType === 'PROJECT');
            return (
              <div className="space-y-3">
                {marked.length === 0 ? (
                  <p className="text-[13px] text-ink-mute">
                    დავალება და პროექტი ჯერ არ არის მონიშნული.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {marked.map((r) => (
                      <Badge key={r.id} tone="brand">
                        ✓ {STAGE_RESULT_TYPE_LABEL[r.resultType as StageResultType]}
                        {r.label ? ` · ${r.label}` : ''}
                      </Badge>
                    ))}
                  </div>
                )}
                <CertificateLine
                  rows={rows}
                  pct={stagePct(enrollment, enrollment.currentStage, rows)}
                  dropped={Boolean(enrollment.dropped)}
                  showManual={false}
                />
              </div>
            );
          })()
        ) : stages.length === 0 ? (
          <EmptyState title="კურსს ეტაპები არ აქვს" />
        ) : (
          <ul className="space-y-2">
            {stages.map((stage) => {
              const results = byStage.get(stage) ?? [];
              return (
                <li
                  key={stage}
                  className="flex flex-wrap items-start gap-3 rounded-lg border border-line px-3.5 py-2.5"
                >
                  <span
                    className={clsx(
                      'grid size-8 shrink-0 place-items-center rounded-full text-[12px] font-semibold',
                      stage === enrollment.currentStage
                        ? 'bg-brand-50 text-brand-700'
                        : 'bg-canvas text-ink-soft',
                    )}
                    title={stage === enrollment.currentStage ? 'მიმდინარე ეტაპი' : 'ისტორია'}
                  >
                    {stage}
                  </span>

                  <div className="min-w-0 flex-1 space-y-2">
                    {results.length === 0 ? (
                      <span className="text-[13px] text-ink-mute">შედეგი ჯერ არ არის</span>
                    ) : (
                      <>
                        {results.map((result) => (
                          <div key={result.id} className="flex flex-wrap items-start gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <Badge tone="brand">
                                  {STAGE_RESULT_TYPE_LABEL[result.resultType as StageResultType]}
                                </Badge>
                                {result.resultType === 'SCORE' && result.score !== null && (
                                  <span className="tabular text-[13px] font-semibold text-ink">
                                    {result.score}
                                  </span>
                                )}
                                {result.label && (
                                  <span className="text-[13px] text-ink-soft">{result.label}</span>
                                )}
                                {result.attendancePct !== null && (
                                  <span className="tabular text-[12px] text-ink-mute">
                                    დასწრება {result.attendancePct}%
                                  </span>
                                )}
                                {result.isHistorical && <Badge>ისტორია</Badge>}
                              </div>
                              {result.comment && (
                                <p className="mt-1 text-[12px] text-ink-mute">{result.comment}</p>
                              )}
                              <p className="mt-0.5 text-[11px] text-ink-mute">
                                {result.gradedBy?.fullName ?? '—'} · {formatDate(result.gradedAt)}
                              </p>
                            </div>

                            {canEdit && (
                              <button
                                onClick={() => setToDelete(result)}
                                aria-label="წაშლა"
                                className="rounded-md p-1.5 text-ink-mute transition hover:bg-danger-soft hover:text-danger"
                              >
                                <Trash2 className="size-4" />
                              </button>
                            )}
                          </div>
                        ))}
                        <CertificateLine
                          rows={results}
                          pct={stagePct(enrollment, stage, results)}
                          dropped={Boolean(enrollment.dropped) && stage === enrollment.currentStage}
                          showManual
                        />
                      </>
                    )}
                  </div>

                  {canEdit && (
                    <button
                      onClick={() => setEditing(stage)}
                      aria-label={results.length ? 'შეფასების რედაქტირება' : 'შედეგის დამატება'}
                      title={results.length ? 'შეფასების რედაქტირება' : 'შედეგის დამატება'}
                      className="rounded-md p-1.5 text-ink-mute transition hover:bg-canvas hover:text-brand-600"
                    >
                      {results.length ? <Pencil className="size-4" /> : <Plus className="size-4" />}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <StageSetModal
        studentId={studentId}
        enrollmentId={enrollment.id}
        stage={editing}
        rows={editingRows}
        pct={editing !== null ? stagePct(enrollment, editing, editingRows) : null}
        dropped={Boolean(enrollment.dropped) && editing === enrollment.currentStage}
        isStaff={!lecturerMode}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={() => void handleDelete()}
        title="შედეგის წაშლა"
        message={`წაიშლება ეტაპი ${toDelete?.stage ?? ''}-ის ჩანაწერი „${
          toDelete ? STAGE_RESULT_TYPE_LABEL[toDelete.resultType as StageResultType] : ''
        }".`}
        confirmLabel="წაშლა"
        danger
        busy={remove.isPending}
      />
    </>
  );
}

// ============================================================

/** რა ეწერება სერტიფიკატზე — საერთაშორისო ცალკე გამოირჩევა */
function CertificateLine({
  rows,
  pct,
  dropped,
  showManual,
}: {
  rows: StageResult[];
  pct: number | null;
  /** შეწყვეტილი — ავტომატურად „არა“ */
  dropped: boolean;
  showManual: boolean;
}) {
  const cert = effectiveCertificate(rows, pct, { dropped });
  // ეტაპი ჯერ არ შეფასებულა — სტატუსი არ ენიჭება
  if (!cert) return null;
  const international = cert.kind === 'INTERNATIONAL';
  const none = cert.kind === 'NONE';
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span
        className={clsx(
          'inline-flex items-center gap-1 rounded px-2 py-0.5 text-[12px] font-medium',
          international ? 'bg-brand-600 text-white' : none ? 'bg-canvas text-ink-mute' : 'bg-brand-100 text-brand-700',
        )}
      >
        {international ? <Globe className="size-3" /> : <Award className="size-3" />}
        სერტიფიკატი: {cert.text}
      </span>
      {showManual && cert.manual && <span className="text-[11px] text-ink-mute">ხელით</span>}
    </div>
  );
}

function StageSetModal({
  studentId,
  enrollmentId,
  stage,
  rows,
  pct,
  dropped,
  isStaff,
  onClose,
}: {
  studentId: string;
  enrollmentId: string;
  stage: number | null;
  rows: StageResult[];
  pct: number | null;
  /** შეწყვეტილი — ავტომატური სტატუსი „არა“ */
  dropped: boolean;
  /** მენეჯერი/ადმინი: სერტიფიკატის ხელით არჩევა */
  isStaff: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const save = useSaveStageSet(studentId);

  const [task, setTask] = useState(false);
  const [taskLabel, setTaskLabel] = useState('');
  const [project, setProject] = useState(false);
  const [projectLabel, setProjectLabel] = useState('');
  const [comment, setComment] = useState('');
  /** მენეჯერის არჩეული სტატუსი; სანამ არ შეეხება, ჩამონათვალი ლოგიკას მიჰყვება */
  const [certChoice, setCertChoice] = useState('');
  const [certTouched, setCertTouched] = useState(false);

  const existingManual = rows.find((r) => r.certStatus?.trim())?.certStatus?.trim() ?? '';
  // ცნობილი ტექსტი (მაგ. CSV-დან იმპორტირებული) ჩამონათვალში თავის სტატუსად ჩანს
  const existingKind = certKindOf(existingManual);
  const initialChoice = existingKind ? CERT_LABEL[existingKind] : existingManual;

  useEffect(() => {
    if (stage === null) return;
    const taskRow = rows.find((r) => r.resultType === 'TASK');
    const projectRow = rows.find((r) => r.resultType === 'PROJECT');
    setTask(Boolean(taskRow));
    setTaskLabel(taskRow?.label ?? '');
    setProject(Boolean(projectRow));
    setProjectLabel(projectRow?.label ?? '');
    setComment(taskRow?.comment ?? projectRow?.comment ?? '');
    setCertChoice(initialChoice);
    setCertTouched(false);
    // ფორმა ივსება მხოლოდ გახსნისას — შემდეგ რედაქტირებას არ გადავაწეროთ
  }, [stage]);

  // რაც ლოგიკით ეკუთვნის (შესრულებული + დასწრება) — მოსანიშნებთან ერთად იცვლება
  const auto = deriveCertificate({ task, project, pct, dropped });
  // „ავტომატური" აღარ ჩანს: ჩამონათვალში წინასწარ ლოგიკის სტატუსია მონიშნული,
  // ხელით არჩეული (ან CSV-ის) ტექსტი კი — თუ არსებობს
  const shownCert = certTouched || initialChoice ? certChoice : CERT_LABEL[auto];

  // ხელით ჩაწერილი უცნობი ტექსტი (მაგ. CSV-დან) არჩევანში უნდა დარჩეს, თორემ დაიკარგება
  const certOptions = [
    ...CERT_KINDS.map((k) => ({ value: CERT_LABEL[k], label: CERT_LABEL[k] })),
    ...(existingManual && !existingKind
      ? [{ value: existingManual, label: `${existingManual} (არსებული)` }]
      : []),
  ];

  const canSave = task || project;

  async function submit() {
    if (stage === null || !canSave) return;
    try {
      await save.mutateAsync({
        enrollmentId,
        stage,
        task: task ? { label: taskLabel } : null,
        project: project ? { label: projectLabel } : null,
        comment,
        // სტატუსი მხოლოდ მაშინ იგზავნება, როცა მენეჯერი ჩამონათვალს შეეხო — უცვლელი
        // ჩანაწერი (მაგ. CSV-ის ტექსტი) ზუსტად ისე რჩება. ლოგიკის ტოლი არჩევანი არ
        // ინახება ტექსტად ('' ) — ასე დასწრების ცვლილებას მომავალშიც მიჰყვება.
        // ქულას ფორმა აღარ ეხება.
        ...(isStaff && certTouched
          ? { certStatus: certChoice === CERT_LABEL[auto] ? '' : certChoice }
          : {}),
      });
      toast.success('შეფასება შენახულია');
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'შენახვა ვერ მოხერხდა');
    }
  }

  return (
    <Modal
      open={stage !== null}
      onClose={onClose}
      size="sm"
      title={`ეტაპი ${stage ?? ''}`}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={save.isPending}>
            გაუქმება
          </Button>
          <Button
            size="sm"
            onClick={() => void submit()}
            loading={save.isPending}
            disabled={!canSave}
          >
            შენახვა
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-3">
          <p className="text-[13px] font-medium text-ink">
            შესრულებულია <span className="font-normal text-ink-mute">— ერთი მაინც</span>
          </p>
          <Checkbox label="დავალება" checked={task} onChange={setTask} />
          {task && (
            <Field
              label="დავალების აღწერა"
              value={taskLabel}
              onChange={(e) => setTaskLabel(e.target.value)}
              placeholder="დავალების აღწერა"
            />
          )}
          <Checkbox label="პროექტი" checked={project} onChange={setProject} />
          {project && (
            <Field
              label="პროექტის აღწერა"
              value={projectLabel}
              onChange={(e) => setProjectLabel(e.target.value)}
              placeholder="პროექტის სახელი"
            />
          )}
          {!canSave && (
            <p className="text-[12px] text-danger">მონიშნე დავალება ან პროექტი</p>
          )}
        </div>

        {isStaff && (
          <Select
            label="სერტიფიკატის სტატუსი"
            value={shownCert}
            onChange={(e) => {
              setCertChoice(e.target.value);
              setCertTouched(true);
            }}
            options={certOptions}
          />
        )}

        <Textarea
          label="კომენტარი"
          rows={2}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />

      </div>
    </Modal>
  );
}
